import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { of, switchMap } from 'rxjs';
import { LoginService } from '../../core/services/login.service';
import { CleverService } from '../../core/services/clever.service';
import { SessionStorageService } from 'src/app/core/services/session-storage.service';
import { Credentials } from '../../core/models/credentials.model';
import * as Sentry from '@sentry/angular';

@Component({
    selector: 'wf-clever',
    templateUrl: './clever.component.html',
    standalone: false
})
export class CleverComponent implements OnInit {
  token: string | null = null;
  invalidToken: string | null = null;
  loadingToken: boolean = false;
  error: {message: string, details: string} | null = null;

  constructor(
    private loginService: LoginService,
    private cleverService: CleverService,
    private sessionStorageService: SessionStorageService,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.loadingToken = true;

    // Get the token/invalidToken params from the route
    this.activatedRoute.queryParams
      .subscribe((params: any) => {
          this.token = params.token;
          this.invalidToken = params.invalidToken
        }
      );

    if (this.invalidToken) {
      this.error = {
        message: 'Could not communicate with Clever',
        details: 'We could not communicate with Clever to get a valid login and retrieve your user info'
      }
      this.loadingToken = false;

      return;
    }

    this.cleverService.getUserInfo(this.token || '').pipe(
      switchMap((userInfoObj: Credentials) => {
        // If the user exists, we will get back a credentials object with the wf username set
        if (userInfoObj.wf.username) {
          return of(userInfoObj);
        }

        // If we have not gotten that in the response, we need to create our user
        return this.cleverService.createNewUser(this.token || '');
      })
    ).subscribe({
      next: (userInfoObj: Credentials) => {
        // At this point, we either found our user (by IdP or email) OR we created our user, so login
        this.loadingToken = false;

        // Store our source token in session storage
        userInfoObj.sso.sourceToken = this.token || '';
        this.login(userInfoObj);
      },
      error: (error: any) => {
        this.sessionStorageService.setUserLoggedIn(false);
        this.error = error.error;
        this.loadingToken = false;

        Sentry.captureException(error, {
          tags: {
            section: 'clever-get-user-info',
          }
        });
      }
    });
  }


  // Login by making request via loginFactory to the web services
  login(credentials: Credentials) {
    this.loginService.loginWithChecks(credentials.wf.username, '', credentials.sso).subscribe({
      next: (response) => {
        this.loadingToken = false ;

        switch (response.status) {
          case 'maintenance':
            this.error = {
              message: 'Maintenance Scheduled',
              details: 'The application is temporarily unavailable due to scheduled maintenance.'
            }
            break ;

          case 'update':
            this.error = {
              message: 'Update Available',
              details: 'A new version of the management application is available. The page will reload shortly...'
            }

            setTimeout(() => {
              window.location.reload();
            }, 1500);

            break ;

          case 'success':
            const { loginResponse } = response ;
            this.error = null ;

            let managementUser = this.sessionStorageService.getUserData();

            if (loginResponse.isLoggedIn)
            {
              // A clever management user successfully logged in, check if it is a free screener (library) user,
              // if so then update oru hubspot contact last login
              const ssoInfo = this.sessionStorageService.getSSOInfo();
              if (ssoInfo && ssoInfo.authorizedBy !== 'district')
              {
                this.cleverService.setCleverFreeScreenerLastLogin(managementUser!.email || '') ;
              }

              this.loginService.redirectAfterLogin(managementUser, loginResponse) ;
            }
        }
      },
      error: (error) => {
        this.error = {
          message: 'Login failed',
          details: 'An error occurred while trying to login to WordFlight'
        }

        Sentry.captureException(error, {
          tags: {
            section: 'clever-login',
          }
        }) ;
      }
    });
  }
}
